import { createTheme, alpha, type Theme, type ThemeOptions } from "@mui/material/styles";
import type { ChipProps } from "@mui/material/Chip";
import { baseTheme, withSchemeTokens } from "./theme";

// Kodakku is named after Psyduck (コダック) — see design doc 021's Kodakku naming note.
// Primary/secondary supplied by design; MUI derives light/dark/contrastText from `main`.
const KODAKKU_PRIMARY = "#F1B800";
const KODAKKU_SECONDARY = "#F7E9A7";

// Three distinct surface tones so nav / canvas / cards read as separate layers instead of one
// flat wash: a warm primary-tinted sidebar, a cool neutral content canvas, and white cards
// (Panel's default tone) lifting off both. Exported for InternalLayout to apply to the sidebar
// directly — not part of MUI's two-slot palette.background, so it's a plain constant, not a
// theme token.
export const KODAKKU_SIDEBAR_BG = alpha(KODAKKU_PRIMARY, 0.07);
const KODAKKU_CANVAS_BG = "#EAF0F5";
const KODAKKU_DARK_CANVAS_BG = "#0F1720";
const KODAKKU_DARK_PAPER_BG = "#18232E";
const MUI_DARK_PALETTE = createTheme({ palette: { mode: "dark" } }).palette;

// Brand display font (public/assets/kodakku.ttf, registered in kodakku-font.css) for headings
// only — body copy stays on Inter for density/readability. Falls back to the same sans stack as
// the public theme if the font file is ever missing.
const KODAKKU_HEADING_FONT =
  '"Inter Variable", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

/**
 * Single dial for Kodakku's overall type size: every variant below is `BASE_PX[variant] *
 * KODAKKU_FONT_SCALE` px. 1 = the tuned base scale below (denser than the public site) — bump
 * this up or down to taste without re-deriving individual variant sizes.
 */
const KODAKKU_FONT_SCALE = 1;

/** Reference sizes (px, at scale 1) for each Typography variant, hand-tuned for a dense admin
 *  console rather than derived from MUI's editorial-scale defaults (h1 96px etc — far too wide a
 *  spread for a compact sidebar/table UI). */
const BASE_PX: Record<
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "subtitle1"
  | "subtitle2"
  | "body1"
  | "body2"
  | "caption"
  | "overline"
  | "button",
  number
> = {
  h1: 28,
  h2: 26,
  h3: 24,
  h4: 20, // PageTitle
  h5: 18,
  h6: 16, // SectionTitle, Statistic value, LoadingTitle
  subtitle1: 14, // Subtitle
  subtitle2: 14,
  body1: 14, // Body
  body2: 13, // Note
  caption: 12, // Meta, Caption, Statistic label
  overline: 11, // SectionLabel
  button: 14,
};

/** px (at scale 1) → rem string, applying KODAKKU_FONT_SCALE, against a 16px root. */
function scaled(basePx: number): string {
  return `${(basePx * KODAKKU_FONT_SCALE) / 16}rem`;
}

const KODAKKU_TYPOGRAPHY = {
  h1: { fontSize: scaled(BASE_PX.h1), fontFamily: KODAKKU_HEADING_FONT },
  h2: { fontSize: scaled(BASE_PX.h2), fontFamily: KODAKKU_HEADING_FONT },
  h3: { fontSize: scaled(BASE_PX.h3), fontFamily: KODAKKU_HEADING_FONT },
  h4: { fontSize: scaled(BASE_PX.h4), fontFamily: KODAKKU_HEADING_FONT }, // PageTitle
  h5: { fontSize: scaled(BASE_PX.h5), fontFamily: KODAKKU_HEADING_FONT },
  h6: { fontSize: scaled(BASE_PX.h6), fontFamily: KODAKKU_HEADING_FONT }, // SectionTitle, Statistic value, LoadingTitle
  subtitle1: { fontSize: scaled(BASE_PX.subtitle1) }, // Subtitle
  subtitle2: { fontSize: scaled(BASE_PX.subtitle2) },
  body1: { fontSize: scaled(BASE_PX.body1) }, // Body
  body2: { fontSize: scaled(BASE_PX.body2) }, // Note
  caption: { fontSize: scaled(BASE_PX.caption) }, // Meta, Caption, Statistic label
  overline: { fontSize: scaled(BASE_PX.overline) }, // SectionLabel
  button: { fontSize: scaled(BASE_PX.button) },
};

/** The focus ring of a field: Kodakku gold instead of the public theme's sage. */
function focusRing({ theme }: { theme: Theme }) {
  return {
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
  };
}

/**
 * Everything Kodakku (the internal human-ops console) sets on top of the public theme, as the
 * light theme: its own identity, distinct from the public site's sage-green editorial theme — a
 * denser type scale (an admin tool is read, not read *to*) and its own primary/secondary palette.
 * The dark theme is these options with only what differs replaced.
 */
const KODAKKU_OPTIONS: ThemeOptions = {
  palette: {
    primary: { main: KODAKKU_PRIMARY },
    secondary: { main: KODAKKU_SECONDARY },
    text: {
      primary: "#1C2733",
      secondary: "#54626F",
    },
    background: {
      paper: "#FFFFFF",
      default: KODAKKU_CANVAS_BG,
    },
    divider: "rgba(30, 45, 60, 0.09)",
  },
  typography: KODAKKU_TYPOGRAPHY,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: KODAKKU_CANVAS_BG },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: { root: focusRing },
    },
  },
};

/**
 * Composed on top of `baseTheme` (not the responsive-scaled `theme` export) via MUI's documented
 * theme-augmentation pattern (`createTheme(existingTheme, overrides)`) — verdicts
 * (brand-independent by design) and shape are inherited untouched; palette, a component style
 * that hardcodes the public theme's sage accent color, and typography are overridden.
 */
export const kodakkuTheme = withSchemeTokens(createTheme(baseTheme, KODAKKU_OPTIONS));

/**
 * The same theme for a browser set to a dark colour scheme: the canvas, cards and text are dark,
 * and what the base theme hardcodes for a light page (the white input fields, the dark text of a
 * soft chip) is turned around. Type scale and gold primary are the light theme's.
 */
export const kodakkuDarkTheme = withSchemeTokens(
  createTheme(baseTheme, {
    ...KODAKKU_OPTIONS,
    palette: {
      ...KODAKKU_OPTIONS.palette,
      mode: "dark",
      // The public palette is computed for a light page, so what MUI derives from the mode (the
      // colour of icons, hovers, disabled controls and disabled text) is taken from MUI's own
      // dark palette.
      action: MUI_DARK_PALETTE.action,
      text: {
        primary: "#E6EDF3",
        secondary: "#9AA8B5",
        disabled: MUI_DARK_PALETTE.text.disabled,
      },
      background: {
        paper: KODAKKU_DARK_PAPER_BG,
        default: KODAKKU_DARK_CANVAS_BG,
      },
      divider: "rgba(230, 237, 243, 0.12)",
    },
    components: {
      ...KODAKKU_OPTIONS.components,
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: KODAKKU_DARK_CANVAS_BG },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            backgroundColor: alpha(theme.palette.common.white, 0.04),
            ...focusRing({ theme }),
          }),
          notchedOutline: {
            borderColor: "rgba(230, 237, 243, 0.2)",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          // A soft chip is a tint of its colour with the colour's light shade as text: the base
          // theme's dark shade would be unreadable on a dark card.
          root: ({ theme, ownerState }: { theme: Theme; ownerState: ChipProps }) => {
            const { color, variant } = ownerState;
            const soft =
              variant === "filled" && color && color !== "default"
                ? {
                    backgroundColor: alpha(theme.palette[color].main, 0.22),
                    color: theme.palette[color].light,
                    ...(ownerState.onDelete || ownerState.clickable
                      ? { "&:hover": { backgroundColor: alpha(theme.palette[color].main, 0.34) } }
                      : {}),
                  }
                : {};
            return {
              fontWeight: 600,
              borderRadius: 4,
              letterSpacing: "0.02em",
              ...soft,
            };
          },
        },
      },
    },
  })
);
